<script setup>
import {Head, Link} from "@inertiajs/vue3";

import PageLayout from "@/Layouts/PageLayout.vue";
import GeneralLayout from "@/Layouts/GeneralLayout.vue";
import PracticeThisCTA from "@/Components/Marketing/PracticeThisCTA.vue";
import {BRAND_NAME, speakingTopicsFAQ} from "@/constants.js";
import FAQ from "@/Components/FAQ.vue";

const MetaData = {
    title: 'Topic Questions – IELTS Speaking Questions Part 1',
    description: 'Discover Part 1 IELTS speaking questions at MockReady. Improve speaking skills with various topic questions. Start excelling now!'
}
const props = defineProps({
    questionTopics: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        default: ''
    }
})
</script>

<template>
    <Head>
        <title>{{ MetaData.title }}</title>
        <meta name="description" :content="MetaData.description">
    </Head>
    <GeneralLayout>
        <template #header>
            <!--            <h1 class="px-2 py-6 text-4xl font-extrabold text-gray-900 dark:text-white">-->
            <!--                {{ title }}</h1>-->
        </template>

        <div class="max-w-6xl mx-auto sm:p-2 md:p-4">
            <h1 class="text-2xl font-bold text-center mb-4">Mastering IELTS Question Topics with MockReady</h1>
            <p class="mb-4">
                Are you preparing for the IELTS exam and want to master diverse question topics? MockReady offers
                strategies, resources, and expert insights to help you master the IELTS speaking questions Part 1 exam
                and achieve the desired results.
            </p>

            <h2 class="text-xl font-semibold mb-3">Preparing for IELTS Question Topics</h2>
            <p class="mb-4">
                The IELTS exam covers various topics like social networking, education, sports, technology, health, and
                more. Excelling in this segment showcases your language proficiency and ability to express yourself
                coherently across various subjects.
            </p>

            <h2 class="text-xl font-semibold mb-3">Why Choose MockReady for IELTS Question Topics Preparation?</h2>
            <ul class="list-disc pl-5 mb-4">
                <li><strong>Diverse Question Topics:</strong> We offer a vast collection of IELTS question topics,
                    ensuring you can practice various subjects.
                </li>
                <li><strong>Expert Guidance:</strong> Expert professional insights and strategies help you tackle
                    different question types.
                </li>
                <li><strong>Structured Practice:</strong> We offer structured practice exercises for each question type
                    and guidelines to help you understand the unique requirements and expectations of the test.
                </li>
                <li><strong>Sample Answers:</strong> Explore sample answers highlighting effective responses to various
                    question topics. Analyzing these answers can enhance your understanding of how to formulate your
                    responses.
                </li>
                <li><strong>Realistic Test Simulations:</strong> Practice under timed conditions to become comfortable
                    with the time constraints you will face on test day.
                </li>
            </ul>

            <h2 class="text-xl font-semibold mb-3">How to Make the Most of MockReady's Question Topics Practice?</h2>
            <ul class="list-disc pl-5 mb-4">
                <li class="mb-4"><strong>Select a Question Topic</strong>: Our extensive collection covers many
                    subjects. Choose a question topic aligning with your practice objectives or interests.
                </li>
                <li class="mb-4"><strong>Practice</strong>: Our practice sessions ensure you cover all aspects of the
                    chosen question topic. Pay attention to time management and coherence in your responses.
                </li>
                <li class="mb-4"><strong>Self-Evaluate</strong>: After completing a practice session, assess your
                    performance based on the guidelines and sample answers provided. Identify areas for improvement and
                    make notes for future practice.
                </li>
                <li class="mb-4"><strong>Repeat and Improve</strong>: Practice different question topics to enhance your
                    proficiency. Concentrate on refining your responses and addressing any feedback received.
                </li>
                <li class="mb-4"><strong>Join the MockReady Community</strong>: Connect with fellow IELTS candidates on
                    our platform. Share experiences, seek advice, and collaborate to enhance your skills collectively.
                </li>
            </ul>
            <h2 class="text-xl font-bold mb-4">Discover Your Key to IELTS Success - Try MockReady Today</h2>
            <p class="mb-8">
                Are you ready to master IELTS speaking questions Part 1 and achieve your desired score? Try MockReady
                today and join thousands of IELTS candidates who have improved their performance and succeeded in the
                IELTS exam through our platform. Sign up and let us be your dedicated partner on your journey to IELTS
                success.
            </p>
            <PracticeThisCTA page-type="question_topics_list" placement="bottom"/>

        </div>

        <div class="max-w-6xl mx-auto sm:py-2 md:py-8">
            <h2 class="text-2xl font-semibold text-center mb-4">IELTS Speaking Part 1 Question Topics</h2>
            <section v-for="questionTopic in questionTopics" class="p-4 bg-gray-50 my-4 dark:bg-gray-900">
                <section class="p-2 lg:p-4 my-3 shadow-sm rounded-lg">
                    <h2 class="py-4 font-semibold text-lg dark:text-white text-primary-600">Topic:
                        {{ questionTopic.title }} Questions</h2>

                    <div v-for="question in questionTopic.questions"
                         class="py-2 px-4 bg-gray-100 rounded-md mb-2">
                        <strong>{{ question.question }}</strong>
                        <!-- answer -->
                        <div class="p-2 my-3">
                            <span class="font-medium">Sample Answer:</span> {{ question.answer }}
                        </div>
                    </div>
                </section>
            </section>

            <FAQ :faqItems="speakingTopicsFAQ"/>
        </div>
    </GeneralLayout>
</template>

<style scoped>

</style>
